import axios from 'axios';

// Configuração base do axios
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar o token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratar erros de respostaen
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token inválido ou expirado - só redireciona se não estiver na página de login
      const isLoginPage = window.location.pathname === '/login';
      if (!isLoginPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ========== AUTH ==========
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
};

// ========== USERS ==========
export const usersAPI = {
  create: (data) => api.post('/users/admin', data),
  list: (params) => api.get('/users/admin', { params }),
  getById: (id) => api.get(`/users/admin/${id}`),
  update: (id, data) => api.patch(`/users/admin/${id}`, data),
  delete: (id) => api.delete(`/users/admin/${id}`)
};

// ========== STORES ==========
export const storesAPI = {
  create: (data) => api.post('/stores', data),
  list: (params) => api.get('/stores', { params }),
  getById: (id) => api.get(`/stores/${id}`),
  update: (id, data) => api.patch(`/stores/${id}`, data),
  delete: (id) => api.delete(`/stores/${id}`)
};

// ========== PRODUCTS ==========
export const productsAPI = {
  create: (data) => api.post('/products', data),
  list: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getBySku: (sku) => api.get(`/products/sku/${sku}`),
  update: (id, data) => api.patch(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`)
};

// ========== PROMOTERS ==========
export const promotersAPI = {
  create: (data) => api.post('/promoters', data),
  list: (params) => api.get('/promoters', { params }),
  getById: (id) => api.get(`/promoters/${id}`),
  getStats: (id) => api.get(`/promoters/${id}/stats`),
  update: (id, data) => api.patch(`/promoters/${id}`, data),
  delete: (id) => api.delete(`/promoters/${id}`)
};

// ========== VISITS ==========
// list(params): aceita status, startDate, endDate, promoterId, page, limit (para relatório)
export const visitsAPI = {
  create: (data) => api.post('/visits', data),
  createRecurring: (data) => api.post('/visits/recurring', data),
  list: (params) => api.get('/visits', { params }),
  getMyVisits: (params) => api.get('/visits/my-visits', { params }),
  getReport: (params) => api.get('/visits/report', { params }),
  getById: (id) => api.get(`/visits/${id}`),
  update: (id, data) => api.patch(`/visits/${id}`, data),
  delete: (id) => api.delete(`/visits/${id}`),
  
  // Check-in/Check-out
  checkIn: (id, data) => api.post(`/visits/${id}/check-in`, data),
  checkOut: (id, data) => api.post(`/visits/${id}/check-out`, data),
  cancel: (id) => api.post(`/visits/${id}/cancel`),
  
  // Fotos e Produtos
  addPhoto: (id, data) => api.post(`/visits/${id}/photos`, data),
  addProduct: (id, data) => api.post(`/visits/${id}/products`, data),
  
  // Aprovação/Rejeição
  approve: (id, data) => api.post(`/visits/${id}/approve`, data),
  reject: (id, data) => api.post(`/visits/${id}/reject`, data),
  respond: (id, data) => api.post(`/visits/${id}/respond`, data),
  
  // Comentários
  addComment: (id, data) => api.post(`/visits/${id}/comments`, data),
  getComments: (id) => api.get(`/visits/${id}/comments`),
  
  // Rastreamento de Localização
  logLocation: (id, data) => api.post(`/visits/${id}/location`, data),
  getLocationHistory: (id) => api.get(`/visits/${id}/location-history`),
  
  // Observações do Supervisor
  addPhotoNotes: (photoId, data) => api.post(`/visits/photos/${photoId}/supervisor-notes`, data),
  addProductNotes: (executionId, data) => api.post(`/visits/products/${executionId}/supervisor-notes`, data)
};

// ========== ALERTS ==========
export const alertsAPI = {
  create: (data) => api.post('/alerts', data),
  list: (params) => api.get('/alerts', { params }),
  getMyAlerts: (params) => api.get('/alerts/my-alerts', { params }),
  getById: (id) => api.get(`/alerts/${id}`),
  markAsRead: (id) => api.post(`/alerts/${id}/read`),
  update: (id, data) => api.patch(`/alerts/${id}`, data),
  delete: (id) => api.delete(`/alerts/${id}`)
};

// ========== REPORTS ==========
export const reportsAPI = {
  // Listagem de relatórios
  list: (params) => api.get('/reports', { params }),
  getById: (id) => api.get(`/reports/${id}`),
  
  // Geração de relatórios
  generate: (promoterId, date) => api.post('/reports/generate', { promoterId, date }),
  generateBatch: (date) => api.post('/reports/generate-batch', { date }),
  generateToday: () => api.post('/reports/generate-today'),
  
  // Preview (debug sem LLM)
  preview: (promoterId, date) => api.get(`/reports/preview/${promoterId}/${date}`),
  
  // Activity logs
  getActivities: (promoterId, date) => api.get(`/reports/activity/${promoterId}/${date}`)
};

// ========== VISIT TASKS ==========
export const visitTasksAPI = {
  create: (data) => api.post('/visit-tasks', data),
  list: (params) => api.get('/visit-tasks', { params }),
  getById: (id) => api.get(`/visit-tasks/${id}`),
  update: (id, data) => api.patch(`/visit-tasks/${id}`, data),
  delete: (id) => api.delete(`/visit-tasks/${id}`),
  getVisitStats: (visitId) => api.get(`/visit-tasks/visit/${visitId}/stats`)
};

// ========== TASK TEMPLATES ==========
export const taskTemplatesAPI = {
  create: (data) => api.post('/task-templates', data),
  list: (params) => api.get('/task-templates', { params }),
  getById: (id) => api.get(`/task-templates/${id}`),
  getByPromoterAndStore: (promoterId, storeId) => api.get(`/task-templates/promoter/${promoterId}/store/${storeId}`),
  update: (id, data) => api.patch(`/task-templates/${id}`, data),
  toggleActive: (id) => api.patch(`/task-templates/${id}/toggle-active`),
  delete: (id) => api.delete(`/task-templates/${id}`)
};

export default api;
